import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { updateGoogleConsent } from "../utils/googleConsent";
import { loadLinkedInTracking, loadLemlistTracking } from "../utils/tracking";
import { COOKIE_STORAGE_KEY, COOKIE_CONSENT, COOKIE_EVENTS } from "../constants/consent";

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const [consentStatus, setConsentStatus] = useState<string | null>(null);

  useEffect(() => {
    // Don't show cookie consent on privacy policy page
    if (location.pathname === "/privacy-policy") {
      setIsVisible(false);
      return;
    }

    // Check if user has already given consent
    const hasConsent = localStorage.getItem(COOKIE_STORAGE_KEY);
    setConsentStatus(hasConsent);
    if (!hasConsent) {
      setIsVisible(true);
      return;
    }

    if (hasConsent === COOKIE_CONSENT.ACCEPTED) {
      updateGoogleConsent(true);
      loadLinkedInTracking();
      loadLemlistTracking();
    }
  }, [location.pathname]);

  useEffect(() => {
    const openCookieSettings = () => {
      if (location.pathname === "/privacy-policy") {
        return;
      }

      setIsVisible(true);
    };

    window.addEventListener(COOKIE_EVENTS.OPEN_SETTINGS, openCookieSettings);

    return () => {
      window.removeEventListener(COOKIE_EVENTS.OPEN_SETTINGS, openCookieSettings);
    };
  }, [location.pathname]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_STORAGE_KEY, COOKIE_CONSENT.ACCEPTED);
    window.dispatchEvent(new Event(COOKIE_EVENTS.CONSENT_CHANGED));
    setConsentStatus(COOKIE_CONSENT.ACCEPTED);
    updateGoogleConsent(true);
    loadLinkedInTracking();
    loadLemlistTracking();
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_STORAGE_KEY, COOKIE_CONSENT.REJECTED);
    window.dispatchEvent(new Event(COOKIE_EVENTS.CONSENT_CHANGED));
    setConsentStatus(COOKIE_CONSENT.REJECTED);
    updateGoogleConsent(false);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Note: This dismisses without setting consent, so popup will appear again on next visit
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-800 text-white shadow-lg border-t border-gray-600">
      <div className="container px-3 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Cookie notice text */}
          <p className="flex-1 m-0 text-sm leading-5 sm:leading-relaxed text-gray-200">
            {consentStatus === COOKIE_CONSENT.ACCEPTED && (
              <>
                Your cookie preferences are currently set to <strong>Accepted</strong>.
                You can change your preference.
              </>
            )}

            {consentStatus === COOKIE_CONSENT.REJECTED && (
              <>
                Your cookie preferences are currently set to <strong>Rejected</strong>.
                You can change your preference.
              </>
            )}

            {!consentStatus && (
              <>
                We use cookies to analyze website traffic, improve your experience, and
                measure site performance. You can accept or reject optional cookies.
              </>
            )}{" "}
            Read our{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              privacy policy
            </a>{" "}
            to learn more.
          </p>

          {/* Buttons + Close */}
          <div className="flex flex-row gap-2 shrink-0 items-center justify-center sm:justify-end">

            <button
              onClick={handleAccept}
              disabled={consentStatus === COOKIE_CONSENT.ACCEPTED}
              className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors duration-200 whitespace-nowrap ${
                consentStatus === COOKIE_CONSENT.ACCEPTED
                  ? "bg-green-600 cursor-default"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {consentStatus === COOKIE_CONSENT.ACCEPTED ? "✓ Accepted" : "Accept"}
            </button>

            <button
              onClick={handleReject}
              disabled={consentStatus === COOKIE_CONSENT.REJECTED}
              className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors duration-200 whitespace-nowrap ${
                consentStatus === COOKIE_CONSENT.REJECTED
                  ? "bg-red-600 cursor-default"
                  : "bg-gray-600 hover:bg-gray-700"
              }`}
            >
              {consentStatus === COOKIE_CONSENT.REJECTED ? "✓ Rejected" : "Reject"}
            </button>

            <button
              onClick={handleDismiss}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-500"
              aria-label="Dismiss cookie notice"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
